import React, {useEffect, useState} from "react";
import {Alert, Button, Drawer, Input, Popconfirm, Space, Spin, Table, Tooltip, message} from "antd";
import {ReloadOutlined} from "@ant-design/icons";
import {ApiLinksService} from "../../../../../shared/api";
import {CopyLine, STEPS, errorOf, formatDuration, showStep, stepValue} from "./linkParts";
import {LinkPeople} from "./LinkPeople";

// steps that open the list of people, and its filter
const PEOPLE_STEPS = {users: 'all', login: 'login', register: 'registered'}

const percent = (value, of) => (of ? `${Math.round((value / of) * 100)}%` : '')

// one link: its addresses, the funnel with the share of each step, and the same by day
export const LinkStats = ({link, canEdit, onClose, onChanged}) => {
  const [days, setDays] = useState(null)
  const [loading, setLoading] = useState(false)
  const [title, setTitle] = useState(link.title)
  const [description, setDescription] = useState(link.description || '')
  const [peopleFilter, setPeopleFilter] = useState(null)
  const [error, setError] = useState(null)

  const load = async (refresh = false) => {
    setLoading(true)
    try {
      const {data} = await ApiLinksService.fetchStats(link.id, refresh)
      setDays(data.days || [])
      onChanged(data)
      setError(null)
    } catch (e) {
      setError(errorOf(e).message || 'Метрика не ответила')
    }
    setLoading(false)
  }

  useEffect(() => {
    load(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link.id])

  const save = async (values) => {
    try {
      const {data} = await ApiLinksService.update(link.id, values)
      onChanged(data)
      message.success('Сохранено')
    } catch (e) {
      message.error(errorOf(e).message || 'Не удалось сохранить')
    }
  }

  const steps = STEPS.filter((s) => stepValue(link, s.key) !== null)
  const people = stepValue(link, 'users')
  const dayRows = (days || []).filter((d) => d.visits).reverse()

  return (
    <Drawer visible width={Math.min(820, window.innerWidth)} title={link.title} onClose={onClose} destroyOnClose>
      <Space direction='vertical' size={16} style={{width: '100%'}}>
        {canEdit && (
          <Input.Group compact>
            <Input style={{width: 'calc(100% - 150px)'}} value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)}/>
            <Button style={{width: 150}} disabled={!title.trim() || title === link.title} onClick={() => save({title})}>Переименовать</Button>
          </Input.Group>
        )}
        {canEdit ? (
          <div>
            <Input.TextArea
              value={description}
              placeholder='Описание: например, текст рассылки'
              maxLength={4000}
              autoSize={{minRows: 2, maxRows: 10}}
              onChange={(e) => setDescription(e.target.value)}
            />
            <Button size='small' style={{marginTop: 6}} disabled={description === (link.description || '')}
              onClick={() => save({description})}>Сохранить описание</Button>
          </div>
        ) : (link.description && <div style={{whiteSpace: 'pre-wrap'}}>{link.description}</div>)}
        <div>
          {link.shortUrl && <CopyLine label='короткая' url={link.shortUrl}/>}
          <CopyLine label='длинная' url={link.longUrl}/>
        </div>
        {!link.metrika && (
          <Alert type='warning' showIcon message={`У сайта ${link.host} не подключена Метрика: считаются только клики по короткой ссылке.`}/>
        )}
        {error && <Alert type='error' showIcon message={error}/>}

        <div className='link-funnel'>
          {steps.map((step) => {
            const value = stepValue(link, step.key)
            return (
              <Tooltip key={step.key} title={PEOPLE_STEPS[step.key] && link.host === 'eci-spb.ru' ? `${step.hint}. Нажмите — кто это` : step.hint}>
                <div
                  className={`link-funnel__step${PEOPLE_STEPS[step.key] && link.host === 'eci-spb.ru' ? ' link-funnel__step--open' : ''}${peopleFilter && PEOPLE_STEPS[step.key] === peopleFilter ? ' link-funnel__step--active' : ''}`}
                  onClick={() => PEOPLE_STEPS[step.key] && link.host === 'eci-spb.ru' && setPeopleFilter(PEOPLE_STEPS[step.key])}
                >
                  <span className='link-funnel__value'>{showStep(step, value)}</span>
                  <span className='link-funnel__title'>{step.title}</span>
                  {people && !['clicks', 'visits', 'users', 'avgTime'].includes(step.key) && <span className='cell-hint'>{percent(value, people)} людей</span>}
                  {step.key === 'avgTime' && link.stats && link.stats.totalTime > 0 && <span className='cell-hint'>всего {formatDuration(link.stats.totalTime)}</span>}
                </div>
              </Tooltip>
            )
          })}
          {loading && <Spin/>}
        </div>
        {peopleFilter && <LinkPeople linkId={link.id} filter={peopleFilter} onFilter={setPeopleFilter}/>}
        {link.clicks && link.clicks.bots > 0 && (
          <div className='cell-hint'>Ещё {link.clicks.bots} открытий роботами мессенджеров (для превью) — в клики не входят. Всего кликов людей: {link.clicks.total}.</div>
        )}

        {link.metrika && (
          <>
            <Space>
              <Button size='small' icon={<ReloadOutlined/>} loading={loading} onClick={() => load(true)}>Обновить из Метрики</Button>
              {link.statsAt && <span className='cell-hint'>обновлено {new Date(link.statsAt).toLocaleString('ru-RU')}</span>}
            </Space>
            <Table
              size='small'
              rowKey='day'
              dataSource={dayRows}
              pagination={{pageSize: 14, hideOnSinglePage: true}}
              locale={{emptyText: days === null ? 'Загрузка…' : 'Переходов по ссылке пока не было'}}
              columns={[
                {title: 'День', dataIndex: 'day', render: (d) => new Date(d).toLocaleDateString('ru-RU')},
                ...STEPS.filter((s) => !['clicks', 'long'].includes(s.key) && stepValue(link, s.key) !== null)
                  .map((s) => ({title: s.title, dataIndex: s.key, align: 'right', render: (v) => showStep(s, v)})),
              ]}
            />
          </>
        )}

        {canEdit && (
          <Popconfirm
            title={link.archived ? 'Вернуть ссылку из архива?' : 'Убрать в архив? Короткая ссылка перестанет работать, длинная — продолжит.'}
            okText={link.archived ? 'Вернуть' : 'В архив'}
            cancelText='Нет'
            onConfirm={() => save({archived: !link.archived})}
          >
            <Button>{link.archived ? 'Вернуть из архива' : 'В архив'}</Button>
          </Popconfirm>
        )}
      </Space>
    </Drawer>
  )
}
